import { z } from "zod";

import type {
  DayAlignedPeriodRangeName,
  PeriodRangeName
} from "../../../../shared/domain/analytics/period-range";
import {
  ANALYTICS_FILTER_OPS,
  ANALYTICS_GRANULARITIES,
  ANALYTICS_VIEW_NAMES,
  type AnalyticsAggregation,
  analyticsColumn,
  analyticsDimensions,
  analyticsMeasures,
  type AnalyticsViewName
} from "./analytics-catalog";
import { optionalQueryParam, PaginationParamsSchema } from "./common.schemas";

/**
 * The analytics query layer's time vocabulary.
 *
 * ⚠️ This is ONE OF TWO time VOCABULARIES. This one serves the analytics query
 * layer (`modules/Analytics`, the public v1 API, and the CLI's `time-period`
 * util). The other is `TIME_PERIOD_PRESETS` in
 * `shared/domain/dashboard/time-period.constants.ts`, which serves the Dashboard
 * domain API (`modules/Dashboard`).
 *
 * They offer different sets: this one has `last_24_hours`, `last_12_months` and
 * `all_time`; that one has `last_180_days` and `last_365_days`, which no analytics
 * query offers.
 *
 * ⚠️ **The two MEET on the public v1 analytics endpoints**, which accept the
 * vocabulary below and hand it to the Dashboard's range computation. So a name
 * this schema accepts must resolve there too, whether or not the Dashboard picker
 * offers it.
 *
 * **What a name MEANS is not decided here.** `shared/domain/analytics/period-range.ts`
 * owns the arithmetic for every name either vocabulary can utter, and both
 * surfaces read it. A period added to the enum below without a spec there does not
 * compile — which is the point, and is why neither file carries its own length
 * table any more.
 */
const TIME_PERIOD_VALUES = [
  "last_24_hours",
  "last_7_days",
  "last_30_days",
  "last_90_days",
  "last_12_months",
  "all_time"
] as const satisfies readonly PeriodRangeName[];

export const TimePeriodSchema = z.enum(TIME_PERIOD_VALUES);

/**
 * The analytics time vocabulary, inferred from the schema rather than restated.
 *
 * A consumer that needs to reason ABOUT periods — map one to a length in days,
 * pick a period that covers another, offer a subset in a picker — needs the union
 * as a type. Re-declaring that union at the call site is how a period gets added
 * here and silently ignored there.
 */
export type TimePeriod = z.infer<typeof TimePeriodSchema>;

/**
 * The vocabulary the THREE DASHBOARD-BACKED v1 analytics routes accept —
 * `/analytics/overview`, `/analytics/export`, `/analytics/feedback`.
 *
 * 🔴 **It is narrower than `TimePeriodSchema` above, and that gap is the whole
 * point of it existing.** Those three routes hand their value to the Dashboard's
 * `TimePeriod.fromPreset`, which can only express a range that is BOUNDED and on
 * UTC DAY boundaries. `all_time` is neither bounded nor day-aligned; `last_24_hours`
 * is hour-aligned. `fromPreset` refuses both with a plain `Error`, which the bare
 * `@Catch()` `SentryFilter` scores 500 — so while these three routes advertised the
 * full vocabulary, two documented query values answered a server fault and paged an
 * engineer. Advertising a value the implementation cannot serve is the defect;
 * refusing it at the edge with a 400 is the contract telling the truth.
 *
 * The query endpoints keep the FULL vocabulary and are unaffected:
 * `/analytics/query/structured` reads `resolvePeriodRange` directly, so an
 * unbounded period is simply no WHERE predicate and an hour-aligned one is an
 * hour-bucketed series. Both are correct there. One vocabulary could never be
 * right for both, which is why there are two.
 *
 * `satisfies readonly DayAlignedPeriodRangeName[]` is the compile-time link, and
 * it is load-bearing rather than decorative: putting `all_time` back in this list
 * is a type error in this file instead of a 500 in production. The same shape
 * `TIME_PERIOD_PRESETS` uses, one property tighter.
 */
export const DASHBOARD_TIME_PERIOD_VALUES = [
  "last_7_days",
  "last_30_days",
  "last_90_days",
  "last_12_months"
] as const satisfies readonly DayAlignedPeriodRangeName[];

export const DashboardTimePeriodSchema = z.enum(DASHBOARD_TIME_PERIOD_VALUES);

/** The vocabulary of the dashboard-backed v1 analytics routes, as a type. */
export type DashboardTimePeriod = z.infer<typeof DashboardTimePeriodSchema>;

export const AnalyticsOverviewParamsSchema = z.object({
  timePeriod: DashboardTimePeriodSchema.default("last_7_days"),
  deploymentId: optionalQueryParam(z.string().uuid())
});

export const TimeSeriesPointSchema = z.object({
  date: z.string(),
  value: z.number()
});

export const BreakdownEntrySchema = z.object({
  entityId: z.string(),
  label: z.string().nullable(),
  value: z.number()
});

export const AnalyticsOverviewSchema = z.object({
  totalConversations: z.number(),
  totalConversationsChange: z.number(),
  totalMessages: z.number(),
  totalMessagesChange: z.number(),
  totalUniqueUsers: z.number(),
  totalUniqueUsersChange: z.number(),
  totalCostUsd: z.number(),
  totalCostChange: z.number(),
  tokenUsage: z.object({
    inputTokens: z.number(),
    outputTokens: z.number()
  }),
  timeSeries: z.object({
    conversationsPerDay: z.array(TimeSeriesPointSchema),
    messagesPerDay: z.array(TimeSeriesPointSchema),
    usersPerDay: z.array(TimeSeriesPointSchema),
    costPerDay: z.array(TimeSeriesPointSchema)
  }),
  byChannel: z.array(BreakdownEntrySchema),
  byDeployment: z.array(BreakdownEntrySchema),
  byModel: z.array(BreakdownEntrySchema)
});

export const ExportAnalyticsParamsSchema = z.object({
  timePeriod: DashboardTimePeriodSchema.default("last_7_days"),
  deploymentId: optionalQueryParam(z.string().uuid())
});

export const FeedbackParamsSchema = PaginationParamsSchema.extend({
  timePeriod: DashboardTimePeriodSchema.default("last_7_days"),
  deploymentId: optionalQueryParam(z.string().uuid()),
  score: z.coerce.number().min(0).max(1).optional(),
  // Case-insensitive keyword filter on the feedback comment text.
  search: optionalQueryParam(z.string().trim().min(1).max(200))
});

/** Body for the ad-hoc analytics query endpoint. A single read-only SELECT/WITH. */
export const AnalyticsQueryBodySchema = z.object({
  query: z.string().min(1).max(10_000)
});

export const AnalyticsQueryFieldSchema = z.object({
  name: z.string()
});

export const AnalyticsQueryResultSchema = z.object({
  rows: z.array(z.record(z.string(), z.unknown())),
  rowCount: z.number(),
  fields: z.array(AnalyticsQueryFieldSchema),
  executionTimeMs: z.number(),
  truncated: z.boolean(),
  error: z.string().optional()
});

export const FeedbackEntrySchema = z.object({
  id: z.string(),
  score: z.number(),
  comment: z.string().nullable(),
  messageContent: z.string(),
  messageId: z.string(),
  agentName: z.string().nullable(),
  deploymentName: z.string().nullable(),
  chatId: z.string().nullable(),
  createdAt: z.coerce.date()
});

/** Response data for `GET /public/v1/analytics/feedback`. */
export const ListFeedbackEntriesResponseSchema = z.array(FeedbackEntrySchema);
export type ListFeedbackEntriesResponse = z.infer<typeof ListFeedbackEntriesResponseSchema>;

// ─── Structured (non-SQL) analytics query (NEX-2478) ─────────────────────────

/** A metric: either `count` (count(*)) or `<agg>:<column>` (e.g. `sum:messageCount`). */
export interface ParsedAnalyticsMetric {
  readonly agg: "count" | AnalyticsAggregation;
  /** Measure column for an aggregate metric; undefined for `count`. */
  readonly column?: string;
  /** Result-set alias: `count` or `<agg>_<column>` (e.g. `sum_messageCount`). */
  readonly alias: string;
}

const METRIC_TOKEN = /^(sum|avg|min|max):([A-Za-z][A-Za-z0-9_]*)$/;

/**
 * Parse a metric token into its aggregation + column + alias, or null if
 * malformed. The column is NOT checked against the catalog here — that is the
 * caller's job (it depends on the selected view). The alias is a safe SQL
 * identifier derived only from the agg + column name.
 */
export function parseAnalyticsMetric(raw: string): ParsedAnalyticsMetric | null {
  const token = raw.trim();
  if (token === "count") return { agg: "count", alias: "count" };
  const match = METRIC_TOKEN.exec(token);
  if (!match) return null;
  const agg = match[1] as AnalyticsAggregation;
  const column = match[2];
  return { agg, column, alias: `${agg}_${column}` };
}

export const AnalyticsStructuredFilterValueSchema = z.union([
  z.string(),
  z.number(),
  z.boolean(),
  z.array(z.union([z.string(), z.number()])).nonempty()
]);

export const AnalyticsStructuredFilterSchema = z.object({
  field: z.string().min(1),
  op: z.enum(ANALYTICS_FILTER_OPS),
  value: AnalyticsStructuredFilterValueSchema
});

/**
 * Body for `POST /analytics/query/structured`. Compiled to allowlisted SQL and
 * run through the same scoped, read-only executor as the ad-hoc SQL endpoint.
 * `superRefine` validates every metric/dimension/filter against the catalog for
 * the selected view, so the builder only ever sees known-safe identifiers.
 */
export const AnalyticsStructuredQueryBodySchema = z
  .object({
    view: z.enum(ANALYTICS_VIEW_NAMES),
    metrics: z.array(z.string().min(1)).min(1).max(10),
    groupBy: z.array(z.string().min(1)).max(4).default([]),
    filters: z.array(AnalyticsStructuredFilterSchema).max(20).default([]),
    granularity: z.enum(ANALYTICS_GRANULARITIES).optional(),
    period: TimePeriodSchema.default("last_30_days"),
    limit: z.number().int().min(1).max(1000).optional(),
    orderBy: z.string().min(1).optional(),
    order: z.enum(["asc", "desc"]).optional()
  })
  .superRefine((q, ctx) => {
    const view = q.view as AnalyticsViewName;
    const dimensions = new Set(analyticsDimensions(view));
    const measures = new Set(analyticsMeasures(view));

    const aliases = new Set<string>();
    q.metrics.forEach((raw, i) => {
      const parsed = parseAnalyticsMetric(raw);
      if (!parsed) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["metrics", i],
          message: `Invalid metric "${raw}". Use "count" or "<agg>:<column>".`
        });
        return;
      }
      if (parsed.agg !== "count" && !measures.has(parsed.column ?? "")) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["metrics", i],
          message: `"${parsed.column}" is not a measure on view "${view}".`
        });
        return;
      }
      if (aliases.has(parsed.alias)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["metrics", i],
          message: `Duplicate metric "${raw}".`
        });
      }
      aliases.add(parsed.alias);
    });

    q.groupBy.forEach((dim, i) => {
      if (!dimensions.has(dim)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["groupBy", i],
          message: `"${dim}" is not a dimension on view "${view}".`
        });
      }
    });

    q.filters.forEach((filter, i) => {
      if (!analyticsColumn(view, filter.field)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["filters", i, "field"],
          message: `"${filter.field}" is not a column on view "${view}".`
        });
        return;
      }
      const isArray = Array.isArray(filter.value);
      if (filter.op === "in" && !isArray) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["filters", i, "value"],
          message: `Operator "in" requires an array value.`
        });
      }
      if (filter.op !== "in" && isArray) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["filters", i, "value"],
          message: `Operator "${filter.op}" requires a scalar value.`
        });
      }
    });

    if (q.orderBy) {
      const orderable =
        aliases.has(q.orderBy) ||
        q.groupBy.includes(q.orderBy) ||
        (q.granularity !== undefined && q.orderBy === "bucket");
      if (!orderable) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["orderBy"],
          message: `"${q.orderBy}" must be a selected metric alias, a groupBy field, or "bucket".`
        });
      }
    }
  });

export type AnalyticsStructuredQueryBody = z.infer<typeof AnalyticsStructuredQueryBodySchema>;
export type AnalyticsStructuredFilter = z.infer<typeof AnalyticsStructuredFilterSchema>;

/** Result of a structured query — the query result plus the SQL that was run. */
export const AnalyticsStructuredQueryResultSchema = AnalyticsQueryResultSchema.extend({
  /** The exact SQL the structured request compiled to (powers "Show SQL"). */
  generatedSql: z.string()
});

export type AnalyticsStructuredQueryResult = z.infer<typeof AnalyticsStructuredQueryResultSchema>;
